import React, { useEffect, useId, useMemo, useState } from 'react'
import { getHexColor, OneColorIconProps } from '@/atoms/utils'

interface GuildIconSingleProps extends OneColorIconProps {
  animateColor?: string
}

export const GuildIconSingle: React.FC<GuildIconSingleProps> = ({
  color = 'black',
  size = 16,
  className,
  animateColor,
}) => {
  const reactId = useId()
  const maskId = useMemo(() => `guild-mask-${reactId.replace(/:/g, '')}`, [reactId])

  const c1 = getHexColor(color) || '#000000'
  const c2 = getHexColor(animateColor || color) || c1

  // Animate via CSS transition (mobile-safe)
  const [fill, setFill] = useState<string>(c1)

  // Reset fill whenever base color changes
  useEffect(() => {
    setFill(c1)
  }, [c1])

  // Trigger animation to target color whenever animateColor changes/is provided
  useEffect(() => {
    if (!animateColor) return
    const id = requestAnimationFrame(() => setFill(c2))
    return () => cancelAnimationFrame(id)
  }, [c2, animateColor])

  const transitionClass = '[transition:fill_2.4s_ease-in-out] motion-reduce:[transition:none]'

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      className={className}
      viewBox="0 0 14 14"
      fill="none"
      role="img"
      aria-hidden="true"
      preserveAspectRatio="xMidYMid meet"
      shapeRendering="geometricPrecision"
    >
      <mask id={maskId} fill="white">
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M2.4585 1.75H11.5418C12.0941 1.75 12.5418 2.19663 12.5418 2.74892V6.93519C12.5418 9.52778 10.4637 12.4444 7.00016 13.4167C3.53662 12.4444 1.4585 9.52778 1.4585 6.93518V2.74874C1.4585 2.19645 1.90621 1.75 2.4585 1.75ZM9.01629 6.52577H7.00571C6.98501 6.52277 6.96431 6.52578 6.94362 6.53178C6.92292 6.53778 6.90518 6.54978 6.8904 6.56478C6.87561 6.57978 6.86378 6.59778 6.85491 6.61578C6.84604 6.63378 6.84309 6.65479 6.84309 6.67579V7.53386C6.84309 7.55486 6.84604 7.57586 6.85491 7.59386C6.86378 7.61186 6.87561 7.62986 6.8904 7.64486C6.90518 7.65987 6.92292 7.66887 6.94066 7.67787C6.96136 7.68387 6.97909 7.68687 6.99979 7.68687H6.99982C7.15952 7.68687 7.73308 7.68687 7.91047 7.69587V8.51793C7.67984 8.59894 7.44035 8.63494 7.19789 8.62894C6.21626 8.60794 5.61308 7.88488 5.61308 6.99081C5.61308 6.10574 6.21626 5.34968 7.18902 5.37068C7.57636 5.37968 7.93412 5.49069 8.23867 5.79372C8.27119 5.81472 8.30963 5.82672 8.34807 5.82672C8.3865 5.82672 8.42494 5.81472 8.45747 5.79372L9.08134 5.13066C9.09612 5.11566 9.10795 5.09766 9.11682 5.07966C9.12569 5.05866 9.12864 5.03766 9.12864 5.01665C9.12864 4.99565 9.12273 4.97465 9.1109 4.95665C9.10203 4.93865 9.08725 4.92065 9.06951 4.90865C8.52251 4.40461 7.77741 4.10458 7.10328 4.08358C5.50664 4.06258 4.22933 5.38268 4.22933 6.99981C4.22933 8.61994 5.50664 9.87704 7.10328 9.91605C7.79811 9.92805 8.48408 9.76603 9.09908 9.44201C9.11682 9.42701 9.1316 9.40901 9.14047 9.388C9.14934 9.367 9.15525 9.343 9.15525 9.322V6.68479C9.15525 6.64279 9.13751 6.60378 9.1109 6.57678C9.09316 6.54678 9.05473 6.52878 9.01629 6.52577Z"
        />
      </mask>

      {/* Visible shape */}
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M2.4585 1.75H11.5418C12.0941 1.75 12.5418 2.19663 12.5418 2.74892V6.93519C12.5418 9.52778 10.4637 12.4444 7.00016 13.4167C3.53662 12.4444 1.4585 9.52778 1.4585 6.93518V2.74874C1.4585 2.19645 1.90621 1.75 2.4585 1.75ZM9.01629 6.52577H7.00571C6.98501 6.52277 6.96431 6.52578 6.94362 6.53178C6.92292 6.53778 6.90518 6.54978 6.8904 6.56478C6.87561 6.57978 6.86378 6.59778 6.85491 6.61578C6.84604 6.63378 6.84309 6.65479 6.84309 6.67579V7.53386C6.84309 7.55486 6.84604 7.57586 6.85491 7.59386C6.86378 7.61186 6.87561 7.62986 6.8904 7.64486C6.90518 7.65987 6.92292 7.66887 6.94066 7.67787C6.96136 7.68387 6.97909 7.68687 6.99979 7.68687H6.99982C7.15952 7.68687 7.73308 7.68687 7.91047 7.69587V8.51793C7.67984 8.59894 7.44035 8.63494 7.19789 8.62894C6.21626 8.60794 5.61308 7.88488 5.61308 6.99081C5.61308 6.10574 6.21626 5.34968 7.18902 5.37068C7.57636 5.37968 7.93412 5.49069 8.23867 5.79372C8.27119 5.81472 8.30963 5.82672 8.34807 5.82672C8.3865 5.82672 8.42494 5.81472 8.45747 5.79372L9.08134 5.13066C9.09612 5.11566 9.10795 5.09766 9.11682 5.07966C9.12569 5.05866 9.12864 5.03766 9.12864 5.01665C9.12864 4.99565 9.12273 4.97465 9.1109 4.95665C9.10203 4.93865 9.08725 4.92065 9.06951 4.90865C8.52251 4.40461 7.77741 4.10458 7.10328 4.08358C5.50664 4.06258 4.22933 5.38268 4.22933 6.99981C4.22933 8.61994 5.50664 9.87704 7.10328 9.91605C7.79811 9.92805 8.48408 9.76603 9.09908 9.44201C9.11682 9.42701 9.1316 9.40901 9.14047 9.388C9.14934 9.367 9.15525 9.343 9.15525 9.322V6.68479C9.15525 6.64279 9.13751 6.60378 9.1109 6.57678C9.09316 6.54678 9.05473 6.52878 9.01629 6.52577Z"
        fill={fill}
        className={transitionClass}
      />

      {/* Masked tail (uses same fill) */}
      <path
        d="M7.00016 13.4167L6.45965 15.3422C6.81316 15.4415 7.18717 15.4415 7.54068 15.3422L7.00016 13.4167Z"
        fill={fill}
        mask={`url(#${maskId})`}
        className={transitionClass}
      />
    </svg>
  )
}
